/**
 * The IAP that has been generated for a period, if one has.
 *
 * The cover sheet cannot be approved before the plan it covers exists, so "has an IAP been generated" needs a single
 * answer both the IAP card and the cover card agree on. A persisted export in the workspace is that answer: generating
 * writes one, and nothing else produces a file under this name.
 */

import { useMemo } from 'react';

import {
  useExportedFormFiles,
  type ExportedFormFile,
} from 'components/FileManagement/hooks/useExportedFormFiles';
import { useActualIncidentId } from './commonHooks';

/** Matches what `persistIcsFormExport` names an IAP: `IAP-OP{n}-V{version}-{timestamp}.pdf`. */
export function iapExportPattern(periodNumber: number): RegExp {
  return new RegExp(`^IAP-OP${periodNumber}-`);
}

/** The most recently generated IAP for a period, or undefined when none has been. */
export function useGeneratedIapForPeriod(
  periodNumber: number
): ExportedFormFile | undefined {
  const incidentId = useActualIncidentId();
  const { data: exportedForms } = useExportedFormFiles(incidentId);

  return useMemo(() => {
    const pattern = iapExportPattern(periodNumber);
    return (exportedForms ?? [])
      .filter((file) => pattern.test(file.file_name))
      .sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      )
      .at(0);
  }, [exportedForms, periodNumber]);
}
